/**
 * React Query hooks for the signed-in user's own profile photo (C-27).
 *
 * - ``useMyPhoto``: the photo read (null when there is none), fresh for 5
 *   minutes and refetched every 45, well inside the signed URL's 60.
 * - ``useUploadMyPhoto`` / ``useRemoveMyPhoto``: each puts its answer in the
 *   cache and invalidates the read, so the header avatar updates at once. They
 *   are sent once (no retry): a resent upload would spend another decode slot.
 *
 * Every key is the user the JWT names (``currentUser.id``), never an id anyone
 * could override: another sign-in in the same tab never reads the previous
 * person's cached photo.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useTenantContext } from '@/contexts/TenantContext';
import {
  fetchMyPhoto,
  fetchPersonPhoto,
  removeMyPhoto,
  uploadMyPhoto,
  type UserPhoto,
} from '@/services/userPhotoService';

export const PHOTO_STALE_TIME_MS = 5 * 60 * 1000;
export const PHOTO_REFETCH_INTERVAL_MS = 45 * 60 * 1000;

export const userPhotoKeys = {
  all: ['userPhoto'] as const,
  user: (userId: string) => ['userPhoto', userId] as const,
  /** A person's photo as the signed-in viewer reads it (keyed by the viewer, never shared). */
  person: (viewerId: string, canonicalEntityId: string) =>
    ['userPhoto', viewerId, 'person', canonicalEntityId] as const,
};

/** The user the photo calls act on: the JWT's (TenantContext's ``currentUser``). */
function usePhotoUserId(): string {
  const { currentUser } = useTenantContext();
  return currentUser.id;
}

export function useMyPhoto() {
  const userId = usePhotoUserId();
  return useQuery<UserPhoto | null>({
    queryKey: userPhotoKeys.user(userId),
    queryFn: fetchMyPhoto,
    enabled: !!userId,
    staleTime: PHOTO_STALE_TIME_MS,
    refetchInterval: PHOTO_REFETCH_INTERVAL_MS,
    // A failed read shows the person icon at once; the interval or the next focus reads again.
    retry: false,
  });
}

export function useUploadMyPhoto() {
  const qc = useQueryClient();
  const userId = usePhotoUserId();
  return useMutation({
    mutationFn: (file: File) => uploadMyPhoto(file),
    retry: false,
    onSuccess: (data) => {
      const key = userPhotoKeys.user(userId);
      qc.setQueryData(key, data);
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

export function useRemoveMyPhoto() {
  const qc = useQueryClient();
  const userId = usePhotoUserId();
  return useMutation({
    mutationFn: () => removeMyPhoto(),
    retry: false,
    onSuccess: () => {
      const key = userPhotoKeys.user(userId);
      qc.setQueryData(key, null);
      void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/** The photo of the person ``canonicalEntityId`` (People pages): null when they have none. */
export function usePersonPhoto(canonicalEntityId: string | undefined) {
  const viewerId = usePhotoUserId();
  return useQuery<UserPhoto | null>({
    queryKey: userPhotoKeys.person(viewerId, canonicalEntityId ?? ''),
    queryFn: () => fetchPersonPhoto(canonicalEntityId as string),
    enabled: Boolean(canonicalEntityId),
    staleTime: PHOTO_STALE_TIME_MS,
    refetchInterval: PHOTO_REFETCH_INTERVAL_MS,
    retry: false,
  });
}
